// src/components/sections/BlogPost.js
import React, { useEffect ,useState} from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Link,
  Flex,
  Image,
  Divider,
  Avatar,
  Badge,
  Grid,
  GridItem,
  VStack,
  HStack,
  Icon
} from '@chakra-ui/react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { FaArrowCircleLeft, FaHome, FaCalendarAlt, FaUser, FaTags } from 'react-icons/fa';
import { BiBookmark } from 'react-icons/bi';
import InnerPageHero from './InnerPageHero';
import GetInTouch from './GetInTouch';
import { blogPosts } from '../../blogData'; // Import from shared data
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

function BlogPost() {
  const { id } = useParams();
  // const post = blogPosts.find(post => post.id === id);
   const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  
  useEffect(() => {
    const fetchPost = async () => {
      try {
        const docRef = doc(db, 'posts', id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setPost({ id: docSnap.id, ...docSnap.data() });
        }
      } catch (error) {
        console.error("Error fetching post: ", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  if (loading) return <div class="loader"></div>;
  if (!post) return <div>Post not found</div>;

  if (!post) {
    return (
      <Box textAlign="center" py={20}>
        <Heading mb={4}>Post Not Found</Heading>
        <Link as={RouterLink} to="/blog" color="customBlue.500">
          Back to Blog
        </Link>
      </Box>
    );
  }

  // Add this utility function at the top of your file
const truncateTitle = (title, maxWords = 6) => {
  const words = title.split(' ');
  if (words.length > maxWords) {
    return words.slice(0, maxWords).join(' ') + '...';
  }
  return title;
};


  return (
    <div className="app-container">
      <InnerPageHero
        title={truncateTitle(post.title,3)}
        breadcrumbs={[
          { label: 'Home', href: '/', icon: FaHome },
          { label: 'Blog', href: '/blog' },
          { label: truncateTitle(post.title, 4), isCurrentPage: true }
        ]}
      />

      <Box as="section" py={{ base: 8, md: 12 }} bg="customBlue">
        <Container maxW="container.xl">
          <Grid templateColumns={{ base: '1fr', lg: '3fr 1fr' }} gap={8}>
            {/* Main Content */}
            <GridItem>
              <Box bg="customBlue.900" backdropFilter="blur(5px)" borderRadius="xl" boxShadow="sm" p={{ base: 4, md: 8 }}>
                {/* <Link 
                  as={RouterLink} 
                  to="/blog" 
                  color="customBlue.500" 
                  fontWeight="semibold" 
                  mb={6} 
                  display="inline-flex" 
                  alignItems="center"
                  _hover={{ textDecoration: 'none', color: 'customBlue.600' }}
                >
                  <Icon as={FaArrowCircleLeft} mr={2} />
                  Back to Blog
                </Link> */}

                {/* Post Header */}
                <VStack align="start" spacing={4} mb={8}>
                  <Badge 
                    colorScheme="blue" 
                    px={3} 
                    py={1} 
                    borderRadius="full" 
                    fontSize="sm"
                    display="flex"
                    alignItems="center"
                  >
                    <Icon as={BiBookmark} mr={1} />
                    {post.category}
                  </Badge>
                  
                  <Heading 
                    as="h1" 
                    fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }} 
                    fontWeight="bold"
                    fontFamily="heading"
                    lineHeight="tall"
                  >
                    {post.title}
                  </Heading>
                  
                  <Flex 
                    direction={{ base: 'column', sm: 'row' }} 
                    gap={4} 
                    color="gray.500" 
                    fontSize="sm"
                  >
                    <HStack>
                      <Icon as={FaUser} mt={-3}/>
                      <Text>By {post.author}</Text>
                    </HStack>
                    <HStack>
                      <Icon as={FaCalendarAlt} mt={-3}/>
                      <Text>{post.date}</Text>
                    </HStack>
                  </Flex>
                </VStack>
                {/* <VStack align="start" spacing={3}>
                  {[...new Set(blogPosts.map(post => post.category))].map(category => (
                    <Link
                      key={category}
                      as={RouterLink}
                      to={`/blog/category/${encodeURIComponent(category.toLowerCase())}`}
                      display="flex"
                      alignItems="center"
                      _hover={{ color: 'customBlue.500', textDecoration: 'none' }}
                    >
                      <Icon as={BiBookmark} mr={2} />
                      {category} ({blogPosts.filter(p => p.category === category).length})
                    </Link>
                  ))}
                </VStack> */}

                {/* Featured Image */}
                <Image
                  src={post.imageUrl || 'https://via.placeholder.com/800x400'}
                  alt={post.title}
                  w="100%"
                  h="25em"
                  borderRadius="lg"
                  mb={8}
                  objectFit="cover"
                  loading="lazy"
                />

                {/* Post Content */}
                <Box
                  className="blog-content"
                  fontSize={{ base: 'md', md: 'lg' }}
                  lineHeight="tall"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                  sx={{
                    '& h2': {
                      fontSize: 'xl',
                      fontWeight: 'bold',
                      mt: 8,
                      mb: 4,
                      color: 'customBlue.500'
                    },
                    '& p': {
                      mb: 4
                    },
                    '& ol, & ul': {
                      pl: 6,
                      mb: 4
                    },
                    '& li': {
                      mb: 2
                    }
                  }}
                />

                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <Box mt={10}>
                    <Divider mb={6} />
                    <HStack spacing={4}>
                      <Icon as={FaTags} color="gray.500" />
                      {post.tags.map(tag => (
                        <Badge
                          key={tag}
                          px={3}
                          py={1}
                          borderRadius="full"
                          colorScheme="gray"
                          fontSize="sm"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </HStack>
                  </Box>
                )}
              </Box>
            </GridItem>

            {/* Sidebar */}
            <GridItem>
              <Box bg="customBlue.900" borderRadius="lg" boxShadow="sm" p={6} position="sticky" top="20px">
                <Heading as="h3" fontSize="xl" mb={5} color="customBlue.500">
                  About the Author
                </Heading>

                <Flex align="center" mb={6}>
                  <Avatar
                    size="lg"
                    name={post.author}
                    src={post.authorAvatar}
                    mr={4}
                  />
                  <Box>
                    <Text fontWeight="bold" >{post.author}</Text>
                    <Text fontSize="sm" color="gray.500" mt={-4}>Technical Writer</Text>
                  </Box>
                </Flex>

                <Text mb={6}>
                  {post.author} is an experienced developer and technical writer with expertise in web technologies and performance optimization.
                </Text>

                <Divider mb={6} />

                {/* <Heading as="h3" fontSize="xl" mb={4} color="customBlue.500">
                  Categories
                </Heading> */}

                {/* <VStack align="start" spacing={3}>
                  {[...new Set(blogPosts.map(post => post.category))].map(cat => (
                    <Link
                      key={cat}
                      as={RouterLink}
                      to={`/blog/category/${encodeURIComponent(cat.toLowerCase())}`}
                      display="flex"
                      alignItems="center"
                      _hover={{ color: 'customBlue.500', textDecoration: 'none' }}
                    >
                      <Icon as={BiBookmark} mr={2} />
                      {cat}
                    </Link>
                  ))}
                </VStack> */}
              </Box>
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {/* <GetInTouch /> */}
    </div>
  );
}

export default BlogPost;